import { useFocusEffect, useRoute } from "@react-navigation/native";
import AssessmentMenu from "../../../components/navigation/AssessmentMenu/AssessmentMenu";
import { useCallback, useRef } from "react";
import type { RootStackParamList } from "../../../navigation/navigation.types";
import { RouteProp } from "@react-navigation/native";
import { useGetAssessmentQuery } from "@axio/graphql/generated/graphql";
import { formatCountdownFromSeconds, normalizeTimeFormat } from "../../../utils/timeUtils";
import { useTimerAnnouncements, parseDisplayTimeToSeconds } from "../../../hooks/useTimerAnnouncements";

interface AssessmentMenuContainerProps {
  onComplete: () => void;
  handleExitPress: () => void;
  defaultTime: number | null;
  setDisplayTime: (time: string) => void;
  displayTime: string | null;
  activeAssessmentId: string;
  isTerminatingAssessment: boolean;
  hasMicrophoneControls: boolean;
  isSseAssessment?: boolean;
  selectedDeviceId?: string;
  onDeviceSelect?: (deviceId: string) => void;
}

function decrementTime(time: string): string {
  const [minutes, seconds] = time.split(":").map(Number);
  const totalSeconds = minutes * 60 + seconds - 1;
  return formatCountdownFromSeconds(totalSeconds);
}

function AssessmentMenuContainer({
  onComplete,
  handleExitPress,
  defaultTime,
  setDisplayTime,
  displayTime,
  activeAssessmentId,
  isTerminatingAssessment,
  hasMicrophoneControls,
  isSseAssessment = false,
  selectedDeviceId = "",
  onDeviceSelect,
}: AssessmentMenuContainerProps) {
  const route = useRoute<RouteProp<RootStackParamList, "Custom Course Assessment">>();
  const hasMounted = useRef<boolean>(false);
  const hasTimeLimit = typeof defaultTime === "number" && defaultTime > 0;
  const assessmentId = route.params?.assessment_id;
  const {
    announcement: timerAnnouncement,
    isAssertive: timerAnnouncementAssertive,
    checkAnnouncement,
    resetAnnouncements,
  } = useTimerAnnouncements();

  const { data: assessment } = useGetAssessmentQuery({
    variables: {
      assessmentId,
    },
    skip: !assessmentId,
  });

  const moduleName = assessment?.getAssessment?.data?.moduleHasAssessment?.title;

  useFocusEffect(
    useCallback(() => {
      if (!activeAssessmentId || !hasTimeLimit) {
        return;
      }

      if (activeAssessmentId !== assessmentId) {
        console.error("Assessment ID mismatch");
        setDisplayTime(normalizeTimeFormat(`${defaultTime ?? 0}:00`));
        return;
      }

      resetAnnouncements();
      checkAnnouncement(parseDisplayTimeToSeconds(displayTime));

      const intervalId = setInterval(() => {
        const newTime = displayTime === "00:00" ? "00:00" : decrementTime(displayTime);

        if (newTime === "00:00") {
          clearInterval(intervalId);
        }

        const updatedSeconds = parseDisplayTimeToSeconds(newTime);
        checkAnnouncement(updatedSeconds);
        setDisplayTime(normalizeTimeFormat(newTime));
      }, 1000);

      return () => clearInterval(intervalId);
    }, [
      assessmentId,
      activeAssessmentId,
      displayTime,
      defaultTime,
      hasTimeLimit,
      checkAnnouncement,
      resetAnnouncements,
    ])
  );

  useFocusEffect(
    useCallback(() => {
      if (!hasMounted.current) {
        hasMounted.current = true;
        return;
      }

      if (displayTime === "00:00" && !isTerminatingAssessment) {
        onComplete();
      }
    }, [displayTime, isTerminatingAssessment])
  );

  return (
    <AssessmentMenu
      handleExitPress={handleExitPress}
      remainingTime={hasTimeLimit ? displayTime : null}
      moduleName={moduleName}
      hasMicrophoneControls={hasMicrophoneControls}
      timeIsLoading={hasTimeLimit && !displayTime}
      isSseAssessment={isSseAssessment}
      selectedDeviceId={selectedDeviceId}
      onDeviceSelect={onDeviceSelect}
      timerAnnouncement={timerAnnouncement}
      timerAnnouncementAssertive={timerAnnouncementAssertive}
    />
  );
}

export default AssessmentMenuContainer;
